import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { FOUNDATION_INFO, CERTIFICATES } from "@/lib/initiatives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Phone, Mail, MapPin, FileText, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — we'll write back within 2 working days.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-py">
      <div className="mx-auto max-w-7xl container-x grid md:grid-cols-2 gap-12">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">Contact</div>
          <h1 className="mt-3 font-serif-display text-4xl md:text-6xl text-[#0A2540]">
            We would love to hear from you.
          </h1>
          <p className="mt-4 text-[#4A5568] max-w-lg">
            Reach out for volunteering, media, partnerships, donations or 80G queries —
            we usually respond within two working days.
          </p>

          <ul className="mt-10 space-y-4">
            <li className="flex items-start gap-3 text-sm">
              <MapPin className="w-4 h-4 mt-1 text-[#D4AF37]" />
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#4A5568]">Address</div>
                <div className="text-[#0A2540]">{FOUNDATION_INFO.address}</div>
              </div>
            </li>
            <li className="flex items-start gap-3 text-sm">
              <Phone className="w-4 h-4 mt-1 text-[#D4AF37]" />
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#4A5568]">Phone</div>
                <a className="text-[#0A2540] hover:underline" href={`tel:${FOUNDATION_INFO.phone}`}>
                  {FOUNDATION_INFO.phone}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-3 text-sm">
              <Mail className="w-4 h-4 mt-1 text-[#D4AF37]" />
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#4A5568]">Email</div>
                <a className="text-[#0A2540] hover:underline" href={`mailto:${FOUNDATION_INFO.email}`}>
                  {FOUNDATION_INFO.email}
                </a>
              </div>
            </li>
          </ul>

          <div className="mt-10 rounded-2xl border border-[#0A2540]/10 p-5 bg-white">
            <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">Compliance documents</div>
            <div className="mt-3 flex flex-wrap gap-3">
              <a
                href={CERTIFICATES.reg_80g_pdf}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cert-80g-link"
                className="inline-flex items-center gap-2 text-sm rounded-full border border-[#0A2540]/20 px-4 py-2 hover:border-[#0A2540] hover:bg-[#0A2540] hover:text-white transition-colors"
              >
                <FileText className="w-4 h-4" /> 80G certificate
              </a>
              <a
                href={CERTIFICATES.reg_12a_pdf}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cert-12a-link"
                className="inline-flex items-center gap-2 text-sm rounded-full border border-[#0A2540]/20 px-4 py-2 hover:border-[#0A2540] hover:bg-[#0A2540] hover:text-white transition-colors"
              >
                <FileText className="w-4 h-4" /> 12A certificate
              </a>
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl bg-white border border-[#0A2540]/10 p-8 md:p-10 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Full name*</Label>
              <Input required value={form.name} onChange={set("name")} data-testid="contact-name" className="mt-2 h-11" />
            </div>
            <div>
              <Label>Email*</Label>
              <Input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" className="mt-2 h-11" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="mt-2 h-11" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="mt-2 h-11" />
            </div>
          </div>
          <div>
            <Label>Message*</Label>
            <Textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className="mt-2" />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="rounded-full bg-[#0A2540] text-white hover:bg-[#D4AF37] hover:text-[#0A2540] px-8 py-6 text-base font-medium"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send message"}
          </Button>
        </form>
      </div>
    </section>
  );
}
